/**
 * Image Carousel Component
 * Custom Alpine.js carousel with true crossfade
 */

window.ImageCarousel = ({
  autoplay = false,
  autoplayInterval = null,
  firstAutoplayInterval = null,
  totalSlides = 0
}) => ({
  autoplay,
  autoplayInterval,
  firstAutoplayInterval,
  totalSlides,
  currentIndex: 0,
  isFirstAutoplay: true,
  autoplayTimer: null,
  containerHeight: 0,
  hasTransitioned: false,
  resizeHandler: null,

  init() {
    // Early return if no slides
    if (this.totalSlides === 0) return;

    // Set initial container height from first slide
    this.setContainerHeight();

    // Add no-transition class to first slide on initial load
    const firstSlide = this.$refs.slides?.children[0];
    if (firstSlide) {
      firstSlide.classList.add('no-transition');
    }

    // Start autoplay if enabled
    if (this.autoplay) {
      this.startAutoplay();
    }

    // Store bound resize handler for cleanup
    this.resizeHandler = () => {
      this.setContainerHeight();
    };

    // Listen for window resize to adjust height
    window.addEventListener('resize', this.resizeHandler);

    if (Shopify.designMode) {
      this._setUpDesignModeHandlers();
    }
  },

  setContainerHeight() {
    // Get the active slide's height
    const activeSlide = this.$refs.slides?.children[this.currentIndex];
    if (activeSlide) {
      this.containerHeight = activeSlide.offsetHeight;
    }
  },

  goToSlide(index, isUserClick = false) {
    if (index === this.currentIndex) return;

    // Remove no-transition class after first transition
    if (!this.hasTransitioned) {
      this.hasTransitioned = true;
      const firstSlide = this.$refs.slides?.children[0];
      if (firstSlide) {
        firstSlide.classList.remove('no-transition');
      }
    }

    this.currentIndex = index;

    // Update height for new slide
    this.$nextTick(() => {
      this.setContainerHeight();
    });

    // If user manually clicked, stop autoplay
    if (isUserClick && this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
      this.autoplay = false;
    }
  },

  startAutoplay() {
    const interval = this.isFirstAutoplay && this.firstAutoplayInterval
      ? this.firstAutoplayInterval
      : this.autoplayInterval;

    this.autoplayTimer = setTimeout(() => {
      this.nextSlide();

      // After first advance, use normal interval
      if (this.isFirstAutoplay && this.firstAutoplayInterval) {
        this.isFirstAutoplay = false;
      }

      this.startAutoplay();
    }, interval);
  },

  resetAutoplay() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    if (this.autoplay) {
      this.startAutoplay();
    }
  },

  nextSlide(isUserClick = false) {
    if (this.totalSlides === 0) return;
    const nextIndex = (this.currentIndex + 1) % this.totalSlides;
    this.goToSlide(nextIndex, isUserClick);
  },

  prevSlide(isUserClick = false) {
    if (this.totalSlides === 0) return;
    const prevIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
    this.goToSlide(prevIndex, isUserClick);
  },

  _setUpDesignModeHandlers() {
    this.$root.addEventListener('slideshow-go', (e) => {
      if (!e.detail) return;
      this.goToSlide(parseInt(e.detail, 10));
    });
  },

  destroy() {
    // Clean up autoplay timer
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
    }

    // Remove window resize event listener to prevent memory leak
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
  }
});
